import { cn } from "../../lib/utils";
import { getPayloadConfigFromPayload, useChartContext } from "./context";

/** One entry of the recharts legend payload, as passed to a custom legend content. */
export interface ChartLegendPayloadItem {
  value?: string | number;
  dataKey?: string | number;
  color?: string;
}

export interface ChartLegendContentProps {
  className?: string;
  hideIcon?: boolean;
  payload?: ChartLegendPayloadItem[];
  verticalAlign?: "top" | "bottom";
  /** Payload key naming each entry's series (falls back to the entry's dataKey). */
  nameKey?: string;
}

/**
 * Styled legend body for charts — pass as `content` of `Chart.Legend`. Series labels and swatch
 * colors come from the surrounding `Chart` config.
 */
export const ChartLegendContent = ({
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey,
}: ChartLegendContentProps) => {
  const { config } = useChartContext();

  if (!payload?.length) {
    return null;
  }

  return (
    <div
      data-slot="chart-legend-content"
      className={cn("flex items-center justify-center gap-4", verticalAlign === "top" ? "pb-3" : "pt-3", className)}
    >
      {payload.map((item) => {
        const key = `${nameKey || item.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);

        return (
          <div
            key={item.value}
            className={cn("[&>svg]:text-muted-foreground flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3")}
          >
            {itemConfig?.icon && !hideIcon ? (
              <itemConfig.icon />
            ) : (
              <div className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} />
            )}
            {itemConfig?.label}
          </div>
        );
      })}
    </div>
  );
};
